import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./shadcn/select";
import { Popover, PopoverContent, PopoverTrigger } from "./shadcn/popover";
import { cn } from "../lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "./shadcn/avatar";

export type AssigneeOption = {
  value: string;
  label: string;
  email?: string;
  avatarUrl?: string;
};

export interface AssigneeSelectV2Props {
  value?: any; // string | string[] | immutable | array of id/objects
  onChange: (ids: string[] | string) => void;
  multiple?: boolean;
  placeholder?: string;
  autoOpen?: boolean;
  className?: string;
  noBorder?: boolean; // Hide border by default
  customTrigger?: React.ReactNode; // Custom trigger element to replace default button
  height?: string; // Custom height for trigger button (e.g., "h-7", "h-[50px]")
  showTriggerHeight?: boolean; // Whether to adjust PopoverContent position based on trigger height
  hideIndicatorUntilHover?: boolean; // Hide the select indicator icon until the parent cell is hovered
}

const toPlain = (v: any) => (typeof v?.toJS === "function" ? v.toJS() : v);
export const toIdArray = (v: any): string[] => {
  if (!v) return [];
  const normArr = (arr: any[]): string[] => {
    if (!Array.isArray(arr)) return [];
    if (arr.length === 0) return [];
    if (typeof arr[0] === "string") return arr as string[];
    return arr
      .map((it: any) => {
        if (!it) return "";
        if (typeof it === "string") return it;
        if (typeof it?.get === "function") return String(it.get("id") ?? it.get("value") ?? "");
        return String(it.id ?? it.value ?? "");
      })
      .map((s) => String(s).trim())
      .filter(Boolean);
  };
  if (Array.isArray(v)) return normArr(v);
  if (typeof v?.toJS === "function") {
    const x = v.toJS();
    if (Array.isArray(x)) return normArr(x);
    if (typeof x === "string")
      return x
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    return [];
  }
  if (typeof v === "string")
    return v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
};

/**
 * AssigneeSelectV2 - Sử dụng shadcn/ui Select thay thế cho react-select
 *
 * Features:
 * - Single select: Sử dụng Radix UI Select
 * - Multi select: Sử dụng Popover + Checkbox (vì Radix UI Select không hỗ trợ multi-select)
 * - Tương thích với API của AssigneeSelect cũ
 * - Hỗ trợ Immutable.js
 * - Hiển thị avatar và email
 */
const AssigneeSelectV2: React.FC<AssigneeSelectV2Props> = ({
  value,
  onChange,
  multiple = true,
  placeholder = "Search users...",
  autoOpen = false,
  className = "",
  noBorder = true, // Default: no border
  customTrigger,
  height,
  showTriggerHeight = false,
  hideIndicatorUntilHover = false,
}) => {
  // members from store
  const membersFromStore = useSelector(
    (state: any) => state.projectReducer.getIn(["project", "members"]) || []
  );

  const options = useMemo<AssigneeOption[]>(() => {
    const arr = (membersFromStore as any)?.toArray?.()
      ? (membersFromStore as any).toArray()
      : toPlain(membersFromStore) || [];
    return (arr as any[]).map((m) => {
      const getField = (field: string) => (m?.get ? m.get(field) : (m as any)?.[field]);

      const value =
        getField("accountId") ?? getField("id") ?? (m as any)?.accountId ?? (m as any)?.id ?? "";

      const displayName = getField("displayName") ?? getField("name") ?? getField("email") ?? value;

      const email = getField("email") ?? (m as any)?.email ?? "";
      const avatarUrl = getField("avatarUrl") ?? (m as any)?.avatarUrl ?? "";

      return {
        value: String(value),
        label: String(displayName),
        email: String(email),
        avatarUrl: String(avatarUrl),
      };
    });
  }, [membersFromStore]);

  const selectedIds = useMemo(() => toIdArray(value), [value]);
  const selectedOptions = useMemo(() => {
    const map = new Map(options.map((o) => [o.value, o]));
    return selectedIds.map((id) => map.get(id)).filter(Boolean) as AssigneeOption[];
  }, [options, selectedIds]);

  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [triggerHeight, setTriggerHeight] = useState<number>(28); // Default h-7 = 28px
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(!!autoOpen);
  }, [autoOpen]);

  useEffect(() => {
    if (open && inputRef.current && multiple) {
      // Delay focus slightly to avoid blocking initial render
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
    if (!open) {
      setSearch("");
    }
  }, [open, multiple]);

  // Calculate trigger height when it's available
  useEffect(() => {
    if (triggerRef.current) {
      setTriggerHeight(triggerRef.current.offsetHeight);
    } else if (height) {
      // Parse height from Tailwind class like "h-7" or "h-[50px]"
      const heightMatch = height.match(/(\d+)/);
      if (heightMatch) {
        setTriggerHeight(parseInt(heightMatch[1]));
      }
    }
  }, [height, open]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.email?.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  // Single select implementation
  if (!multiple) {
    const selectedOption = selectedOptions[0] || null;

    return (
      <div className={cn("w-full", className)}>
        <Select
          value={selectedOption?.value || undefined}
          onValueChange={(val) => {
            onChange(val || "");
            setOpen(false);
          }}
          open={open}
          onOpenChange={setOpen}
        >
          {customTrigger ? (
            <SelectTrigger asChild>
              {React.isValidElement(customTrigger) ? (
                React.cloneElement(customTrigger as any, {
                  onClick: (e: React.MouseEvent) => {
                    setOpen(true);
                    (customTrigger as any).props?.onClick?.(e);
                  },
                  className: cn("cursor-pointer", (customTrigger as any).props?.className),
                })
              ) : (
                <div onClick={() => setOpen(true)} className="cursor-pointer">
                  {customTrigger}
                </div>
              )}
            </SelectTrigger>
          ) : (
            <SelectTrigger
              className={cn(
                height || "h-7",
                "text-xs min-w-[120px]",
                !noBorder && "border border-gray-300",
                noBorder && "border-0",
                "focus:border-0 focus:ring-0 focus-visible:ring-0",
                !selectedOption && "text-gray-500"
              )}
              onClick={() => setOpen(true)}
            >
              <SelectValue placeholder={placeholder}>
                {selectedOption && (
                  <div className="flex items-center gap-2">
                    {selectedOption.avatarUrl && (
                      <Avatar className="h-5 w-5 shrink-0">
                        <AvatarImage src={selectedOption.avatarUrl} alt={selectedOption.label} />
                        <AvatarFallback className="text-[10px]">
                          {selectedOption.label.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <span>{selectedOption.label}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
          )}
          <SelectContent className="p-0 min-w-[282px] z-[100]">
            <div className="px-2 py-1.5 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={placeholder}
                  className="w-full pl-8 pr-2 h-7 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400"
                />
              </div>
            </div>

            <div className="py-1 max-h-64 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No records</div>
              ) : (
                filteredOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                    <div className="flex items-center gap-2 w-full">
                      {option.avatarUrl ? (
                        <Avatar className="h-5 w-5 shrink-0">
                          <AvatarImage src={option.avatarUrl} alt={option.label} />
                          <AvatarFallback className="text-[10px]">
                            {option.label.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="h-5 w-5 rounded-full bg-gray-300 flex items-center justify-center shrink-0">
                          <span className="text-[10px] text-gray-600">
                            {option.label.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-sm text-left">{option.label}</span>
                        {option.email && (
                          <span className="text-xs text-gray-500 truncate text-left">
                            {option.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))
              )}
            </div>
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Multi select implementation using Popover + Checkbox
  const handleToggle = (optionValue: string) => {
    const newSelectedIds = selectedIds.includes(optionValue)
      ? selectedIds.filter((id) => id !== optionValue)
      : [...selectedIds, optionValue];
    onChange(newSelectedIds);
  };

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} modal={false} onOpenChange={setOpen}>
        {customTrigger ? (
          <PopoverTrigger asChild>
            {React.isValidElement(customTrigger) ? (
              React.cloneElement(customTrigger as any, {
                onClick: (e: React.MouseEvent) => {
                  setOpen(true);
                  (customTrigger as any).props?.onClick?.(e);
                },
                className: cn("cursor-pointer", (customTrigger as any).props?.className),
              })
            ) : (
              <div onClick={() => setOpen(true)} className="cursor-pointer">
                {customTrigger}
              </div>
            )}
          </PopoverTrigger>
        ) : (
          <PopoverTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              className={cn(
                "flex w-full min-w-[120px] items-center justify-between rounded-md bg-background px-3 py-1.5 text-xs placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                height || "h-7",
                !noBorder && "border border-gray-300",
                noBorder && "border-0 focus:border-0 active:border-0",
                "focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                selectedOptions.length === 0 && "text-gray-500"
              )}
              onClick={() => setOpen(true)}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {selectedOptions.length === 0 ? (
                  <span className="text-gray-500 text-sm">{placeholder}</span>
                ) : selectedOptions.length === 1 ? (
                  // Single assignee: show avatar + name
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center gap-2 flex-1 min-w-0"
                        title={option.label}
                      >
                        <Avatar className="h-5 w-5 flex-shrink-0">
                          <AvatarImage src={option.avatarUrl} alt={option.label} />
                          <AvatarFallback className="text-xs font-semibold bg-gray-500 text-white">
                            {option.label.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium text-gray-900 truncate leading-5">
                          {option.label}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Multiple assignees: show only avatars in overlapping group
                  <div className="flex items-center -mr-2">
                    {selectedOptions.slice(0, 3).map((option, index) => (
                      <Avatar
                        key={option.value}
                        className={cn(
                          "h-5 w-5 border-2 border-white flex-shrink-0",
                          index > 0 && "-ml-2"
                        )}
                        title={option.label}
                      >
                        <AvatarImage src={option.avatarUrl} alt={option.label} />
                        <AvatarFallback className="text-xs font-semibold bg-gray-500 text-white">
                          {option.label.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                    {selectedOptions.length > 3 && (
                      <div
                        className="h-5 w-5 rounded-full bg-gray-100 border-2 border-white -ml-2 flex items-center justify-center flex-shrink-0"
                        title={selectedOptions
                          .slice(3)
                          .map((o) => o.label)
                          .join(", ")}
                      >
                        <span className="text-xs font-medium text-gray-700">
                          +{selectedOptions.length - 3}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  "flex items-center ml-2 flex-shrink-0 transition-opacity duration-150",
                  hideIndicatorUntilHover && "opacity-0 group-hover/cell:opacity-100"
                )}
              >
                <ChevronsUpDown className="h-3.5 w-3.5 text-gray-400" />
              </div>
            </button>
          </PopoverTrigger>
        )}
        <PopoverContent
          ref={popoverContentRef}
          className="min-w-[282px] p-0 bg-white border border-gray-200 shadow-lg transition-none"
          style={{
            width: "var(--radix-popover-trigger-width)",
            zIndex: 100,
            ...(showTriggerHeight && {
              marginTop: `-${Math.max(triggerHeight - (customTrigger ? 8 : 0), 0)}px`,
            }),
          }}
          align="start"
          side="bottom"
          sideOffset={0}
          onClick={(e) => {
            // Prevent event from bubbling up and closing the popover
            e.stopPropagation();
          }}
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when clicking inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
          onFocusOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when focusing inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when interacting inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
        >
          <div
            onClick={(e) => {
              // Prevent event from bubbling up and closing the popover
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              // Prevent event from bubbling up and closing the popover
              e.stopPropagation();
            }}
          >
            <div className="px-2 py-1.5 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={placeholder}
                  className="w-full pl-8 pr-2 h-7 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400"
                />
              </div>
            </div>

            {/* Options list */}
            <div className="py-1 max-h-64 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No records</div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = selectedIds.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onPointerDownCapture={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseDownCapture={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggle(option.value);
                      }}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-100 transition-colors",
                        isSelected && "bg-blue-50"
                      )}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {option.avatarUrl ? (
                          <Avatar className="h-5 w-5 shrink-0">
                            <AvatarImage src={option.avatarUrl} alt={option.label} />
                            <AvatarFallback className="text-[10px]">
                              {option.label.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div className="h-5 w-5 rounded-full bg-gray-300 flex items-center justify-center shrink-0">
                            <span className="text-[10px] text-gray-600">
                              {option.label.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-sm text-left">{option.label}</span>
                          {option.email && (
                            <span className="text-xs text-gray-500 truncate text-left">
                              {option.email}
                            </span>
                          )}
                        </div>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0 ml-2" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AssigneeSelectV2;
